import { Button, message, TableColumnsType, TableProps, Tag } from 'antd';
import SafeImage from 'components/SafeImage';
import ScrollTable from 'components/ScrollTable';
import StatusButton from 'components/StatusButton';
import dayjs from 'dayjs';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import useModal from 'hooks/useModal';
import { useMemo } from 'react';
import { createConversation } from 'services/api/refund';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { StarRate } from 'views/Review/components/Profile';
import ReviewLineModal from './ReviewLineModal';
import ConversationModal from 'views/Setting/Remind/components/ConversationModal';
import styled from '@emotion/styled';
import useTextsTranslate from './useTextsTranslate';
import { uniq } from 'lodash-es';

type RT = ReviewAnalysis.Review;

function ReviewTable({
  isVoice,
  itemMode = false,
  hasContentOnly = false,
  selectedItemIds,
  stars,
  dataSource: data,
  ...otherProps
}: TableProps<RT> & {
  isVoice?: boolean;
  itemMode?: boolean;
  hasContentOnly?: boolean;
  selectedItemIds?: string[];
  stars?: number[];
}) {
  const texts = useMemo(
    () =>
      uniq(
        data
          ?.map(({ id, reviews }) => {
            const target = reviews.find((_) => _.id === id);
            return target?.text || '';
          })
          .filter((_) => _) || [],
      ),
    [data],
  );
  const {
    t,
    trigger: translateTrigger,
    result: translateResult,
    showTranslate,
  } = useTextsTranslate(texts);

  const reviewLineModalState = useModal<RT>();
  const conversationModalState = useModal<Conversation.Conversation>();

  const reviewLineModal = reviewLineModalState.meta && (
    <ReviewLineModal
      visible={reviewLineModalState.visible}
      onCancel={() => reviewLineModalState.hide()}
      review={reviewLineModalState.meta}
    />
  );

  const conversationModal = conversationModalState.meta && (
    <ConversationModal
      record={conversationModalState.meta}
      visible={conversationModalState.visible}
      onCancel={() => conversationModalState.hide()}
    />
  );

  const { config } = useStoreNameConfig();

  const dataSource = useMemo(() => {
    return data?.filter(({ itemInfo, reviews: [review] }) => {
      return !(
        (hasContentOnly && !review.text && !review.images.length && !review.videos.length) ||
        (selectedItemIds?.length && !selectedItemIds.includes(itemInfo.id)) ||
        (stars?.length && !stars.includes(review.star))
      );
    });
  }, [data, hasContentOnly, selectedItemIds, stars]);

  const colums: TableColumnsType<RT> = [
    itemMode
      ? {
          key: 'sku',
          title: t('sider.SKU信息'),
          width: 200,
          render: (_, { itemInfo: { sku, sellerSku } }) => {
            return (
              <div>
                <div>
                  SKU：
                  {sku}
                </div>
                <div>
                  {t('sider.SKU货号')}：{sellerSku}
                </div>
              </div>
            );
          },
        }
      : {
          key: 'item',
          title: t('商品信息'),
          width: 340,
          render: (_, { itemInfo: { img, name, sku, sellerSku } }) => {
            return (
              <Item>
                <SafeImage width={56} height={56} src={img} />
                <section>
                  <p>{name}</p>
                  <div>
                    SKU：
                    {sku}
                  </div>
                  <div>
                    {t('sider.SKU货号')}：{sellerSku}
                  </div>
                </section>
              </Item>
            );
          },
        },
    {
      key: 'buyer',
      title: t('aide.买家信息'),
      width: 220,
      render: (
        _,
        {
          platform,
          region,
          buyerNick,
          externalBuyerId,
          externalStoreId,
          itemInfo: { storeName, storeAlias },
        },
      ) => {
        return (
          <div>
            <div>
              {platformLabelMap[toLowerCase(platform)]}-{t(`region.${toLowerCase(region)}`)}-
              {isStoreAliasFirst(config, platform, region) ? storeAlias || storeName : storeName}
            </div>
            <div>
              {t('feedback.buyer')}：{buyerNick}
            </div>
            <StatusButton
              className="!pl-0"
              type="link"
              onClick={() =>
                createConversation({
                  externalBuyerId,
                  externalStoreId,
                  buyerNick,
                  region,
                  channel: platform,
                  sourceAction: 'RATING',
                })
                  .then((res) => {
                    conversationModalState.show(res);
                  })
                  .catch(() => {
                    message.error(t('phrase.获取数据失败，请稍后再试'));
                  })
              }
            >
              {t('查看会话')}
            </StatusButton>
          </div>
        );
      },
    },
    {
      key: 'review',
      title: (
        <div className="flex items-center justify-between flex-wrap">
          <div>{isVoice ? t('review.评价内容') : t('差评内容')}</div>
          {texts.length > 0 && <div>{translateTrigger}</div>}
        </div>
      ),
      width: 560,
      render: (_, record) => {
        const { id, reviews } = record;
        const target = reviews.find((_) => _.id === id);
        if (!target) {
          return '-';
        }
        const { text, star, timestamp, commentType } = target;
        const showText = showTranslate ? translateResult?.[text] || text : text;
        return (
          <div>
            {showText ? <div className="line-clamp-5">{showText}</div> : null}
            <section>
              <StarRate disabled value={star} />
              <span style={{ fontSize: 12 }}>
                {dayjs(Number(timestamp)).format('YYYY-MM-DD HH:mm')}
              </span>
              {commentType === 'SELLER' && <Tag>{t('回评')}</Tag>}
            </section>
            <Button className="!pl-0" type="link" onClick={() => reviewLineModalState.show(record)}>
              {t('评价轨迹')}
            </Button>
          </div>
        );
      },
    },
  ];

  const table = (
    <ScrollTable
      rowKey="id"
      dataSource={dataSource}
      columns={colums}
      pagination={
        dataSource?.length && dataSource.length > 50
          ? {
              showQuickJumper: true,
              showSizeChanger: true,
            }
          : false
      }
      {...otherProps}
    />
  );

  return (
    <>
      {table}
      {reviewLineModal}
      {conversationModal}
    </>
  );
}

const Item = styled.div`
  display: flex;
  > div {
    flex: 0 0 auto;
    margin-right: 10px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    overflow: hidden;
    > p {
      margin-bottom: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    > div {
      font-size: 12px;
      color: #84868c;
    }
  }
`;

export default ReviewTable;
